/**
 * De betaalkoppelingen van een verhuurder, en welke deze woning gebruikt.
 *
 * Wat hier gebeurt is geld: dit is de rekening waarop de gast betaalt. Vier dingen
 * volgen daaruit.
 *
 * 1. **De sleutel is nergens terug te zien.** Ook niet na het invoeren, ook niet
 *    gemaskeerd op verzoek. Het invoerveld wordt leeggemaakt zodra hij is
 *    verstuurd; wat er daarna staat is de naam van de rekening en een hint. Een
 *    scherm dat een sleutel toont, is een scherm dat in een schermdeling meegaat.
 * 2. **Test en live staan er met zoveel woorden bij.** Het verschil is aan de
 *    sleutel te zien en dus makkelijk te missen; een testkoppeling die live lijkt,
 *    ontdek je bij de eerste gast die niet kan betalen.
 * 3. **"Online" zonder werkende koppeling is een blokkade, geen waarschuwing.**
 *    De API weigert publiceren, en dat hoort hier al te blijken in plaats van pas
 *    bij het opslaan.
 * 4. **Handmatig betalen is geen tweede keus.** Wie geen account wil openen, hoeft
 *    dat niet; dat staat er ook zo, want het is een volwaardig pad (regel 9).
 */
import { useState } from 'react'
import type { ReactElement } from 'react'

import { useFormatters } from '@siteshowroom/i18n/react'

import { useTranslate } from '../lib/i18n-provider'
import {
  PAYMENT_PROVIDERS,
  connectPaymentAccount,
  disconnectPaymentAccount,
  verifyPaymentAccount,
} from '../lib/payments-client'
import type { PaymentAccount, PaymentFailure, PaymentProvider } from '../lib/payments-client'

export function PaymentAccountsSection({
  accounts,
  canConnect,
  selectedId,
  paymentMode,
  busy,
  onSelect,
  onChanged,
  onFailure,
}: {
  readonly accounts: readonly PaymentAccount[]
  readonly canConnect: boolean
  readonly selectedId: string | null
  readonly paymentMode: string
  readonly busy: boolean
  readonly onSelect: (id: string | null) => void
  readonly onChanged: () => Promise<void>
  readonly onFailure: (error: PaymentFailure) => void
}): ReactElement {
  const t = useTranslate()
  const formatters = useFormatters()

  const [provider, setProvider] = useState<PaymentProvider>('mollie')
  const [label, setLabel] = useState('')
  const [secret, setSecret] = useState('')
  const [working, setWorking] = useState(false)
  const [adding, setAdding] = useState(false)

  const selected = accounts.find((account) => account.id === selectedId) ?? null
  const needsAccount =
    paymentMode === 'online' && (selected === null || selected.status !== 'active')

  async function connect(): Promise<void> {
    setWorking(true)
    const result = await connectPaymentAccount({ provider, label: label.trim(), secret })
    // Het invoerveld gaat hoe dan ook leeg: een sleutel die in een formulier blijft
    // staan, gaat mee in de volgende schermafdruk.
    setSecret('')
    if (result.ok) {
      setLabel('')
      setAdding(false)
      onSelect(result.data.id)
    } else {
      onFailure(result.error)
    }
    await onChanged()
    setWorking(false)
  }

  async function run(work: () => Promise<{ ok: boolean; error?: PaymentFailure }>): Promise<void> {
    setWorking(true)
    const result = await work()
    if (!result.ok && result.error !== undefined) {
      onFailure(result.error)
    }
    await onChanged()
    setWorking(false)
  }

  return (
    <section className="page__section">
      <h2 className="page__sectionTitle">{t('dashboard.payments.title')}</h2>
      <p className="prose">{t('dashboard.payments.intro')}</p>

      {!canConnect && (
        <div className="notice" role="status">
          {/* Eerlijk over wat er niet kan, in plaats van een formulier dat altijd
              faalt. */}
          <p className="notice__title">{t('dashboard.payments.unavailable')}</p>
        </div>
      )}

      <label className="field">
        <span className="field__label">{t('dashboard.payments.field.account')}</span>
        <select
          className="field__input"
          disabled={busy || working}
          onChange={(event) => {
            onSelect(event.target.value === '' ? null : event.target.value)
          }}
          value={selectedId ?? ''}
        >
          <option value="">{t('dashboard.payments.none')}</option>
          {accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {/* Naam en label komen van de provider en de verhuurder: data. */}
              {`${t(`dashboard.payments.provider.${account.provider}`)} — ${
                account.accountName !== '' ? account.accountName : account.accountHint
              }`}
            </option>
          ))}
        </select>
        <span className="field__hint">
          {paymentMode === 'manual'
            ? t('dashboard.payments.manualHint')
            : t('dashboard.payments.onlineHint')}
        </span>
      </label>

      {needsAccount && (
        <div className="notice notice--error" role="alert">
          {/* De API weigert publiceren; dat hoort hier al te blijken. */}
          <p className="notice__title">{t('dashboard.payments.required')}</p>
        </div>
      )}

      {accounts.map((account) => (
        <article className="request-card" key={account.id}>
          <header className="request-card__header">
            <div>
              <h3 className="request-card__title">
                {t(`dashboard.payments.provider.${account.provider}`)}
                {account.accountName !== '' && (
                  <span className="facts__aside">{account.accountName}</span>
                )}
              </h3>
              <span className="facts__aside">
                {account.lastVerifiedAt === null
                  ? t('dashboard.payments.neverVerified')
                  : t('dashboard.payments.verifiedAt', {
                      moment: formatters.formatDateTime(account.lastVerifiedAt),
                    })}
              </span>
              {account.lastError !== '' && (
                // De reden komt van de provider en is dus data.
                <code className="notice__detail">{account.lastError}</code>
              )}
            </div>
            <span className="deadline" data-tone={account.status === 'active' ? 'calm' : 'expired'}>
              {t(`dashboard.payments.status.${account.status}`)}
            </span>
          </header>

          <dl className="facts">
            <div className="facts__row">
              <dt>{t('dashboard.payments.field.mode')}</dt>
              {/* Live of test staat er met zoveel woorden: het verschil ontdek je
                  anders bij de eerste gast die niet kan betalen. */}
              <dd data-tone={account.mode === 'test' ? 'warn' : 'calm'}>
                {t(`dashboard.payments.mode.${account.mode}`)}
              </dd>
            </div>
            <div className="facts__row">
              <dt>{t('dashboard.payments.field.hint')}</dt>
              <dd>{account.accountHint}</dd>
            </div>
          </dl>

          <div className="request-panel__actions">
            <button
              className="button button--quiet"
              disabled={busy || working}
              onClick={() => void run(async () => verifyPaymentAccount(account.id))}
              type="button"
            >
              {t('dashboard.payments.action.verify')}
            </button>
            <button
              className="button button--quiet"
              disabled={busy || working}
              onClick={() => void run(async () => disconnectPaymentAccount(account.id))}
              type="button"
            >
              {t('dashboard.payments.action.disconnect')}
            </button>
          </div>
        </article>
      ))}

      {canConnect && !adding && (
        <div className="request-panel__actions">
          <button
            className="button button--quiet"
            disabled={busy || working}
            onClick={() => {
              setAdding(true)
            }}
            type="button"
          >
            {t('dashboard.payments.action.add')}
          </button>
        </div>
      )}

      {canConnect && adding && (
        <div className="request-panel">
          <h3 className="request-panel__title">{t('dashboard.payments.connect.title')}</h3>
          <p className="request-panel__hint">{t('dashboard.payments.connect.intro')}</p>

          <div className="field-grid">
            <label className="field">
              <span className="field__label">{t('dashboard.payments.field.provider')}</span>
              <select
                className="field__input"
                disabled={working}
                onChange={(event) => {
                  setProvider(event.target.value as PaymentProvider)
                }}
                value={provider}
              >
                {PAYMENT_PROVIDERS.map((option) => (
                  <option key={option} value={option}>
                    {t(`dashboard.payments.provider.${option}`)}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field__label">{t('dashboard.payments.field.label')}</span>
              <input
                className="field__input"
                disabled={working}
                maxLength={100}
                onChange={(event) => {
                  setLabel(event.target.value)
                }}
                type="text"
                value={label}
              />
            </label>
          </div>

          <label className="field">
            <span className="field__label">{t('dashboard.payments.field.secret')}</span>
            <input
              // `password` en `autoComplete="off"`: dit hoort niet in een
              // wachtwoordkluis van een browser terecht te komen, en niet leesbaar
              // op het scherm te staan terwijl iemand meekijkt.
              autoComplete="off"
              className="field__input"
              disabled={working}
              maxLength={500}
              onChange={(event) => {
                setSecret(event.target.value)
              }}
              spellCheck={false}
              type="password"
              value={secret}
            />
            <span className="field__hint">{t(`dashboard.payments.secretHint.${provider}`)}</span>
          </label>

          <p className="request-panel__hint">{t('dashboard.payments.connect.privacy')}</p>

          <div className="request-panel__actions">
            <button
              className="button"
              disabled={working || secret.trim() === ''}
              onClick={() => void connect()}
              type="button"
            >
              {working
                ? t('dashboard.payments.action.connecting')
                : t('dashboard.payments.action.connect')}
            </button>
            <button
              className="button button--quiet"
              disabled={working}
              onClick={() => {
                setAdding(false)
                setSecret('')
              }}
              type="button"
            >
              {t('dashboard.payments.action.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Onderaan en niet als voetnoot: voor veel particulieren is dit het
          antwoord, en niet een compromis. */}
      <p className="prose">{t('dashboard.payments.manualIsFine')}</p>
    </section>
  )
}
